import { OptimizedImage } from "@/components/OptimizedImage";
import { useLanguage } from "@/context/LanguageContext";
import { ServiceBookingOptions } from "@/components/ServiceBookingOptions";
import { STATIC_SERVICE_SEO } from "@/config/serviceSeo";
import { Users, Luggage } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SectionHeading, QuoteButton, Eyebrow, FaqAccordion } from "@/components/shared";
import { IMG } from "@/config/images";
import { Link } from "@/i18n/LocalizedLink";
import { VehicleTariffTable } from "@/components/VehicleTariffTable";
import { FareTerms } from "@/components/FareTerms";
import { TEMPO_TRAVELLER_SCHEMA } from "@/config/structuredData";

const USE_CASES = [
  { title: "Wedding guest transport", desc: "Move baraat and guests together between venues and hotels without juggling multiple cars." },
  { title: "Pilgrimage groups", desc: "Maihar, Chitrakoot, Khajuraho and multi-temple circuits for families and sabhas." },
  { title: "Corporate outings", desc: "Team offsites, site visits and conferences with a planned, prebooked schedule." },
  { title: "Multi-day MP tours", desc: "Khajuraho, Bandhavgarh and heritage circuits with passengers, luggage and planned stops included in the travel enquiry." },
];

const FAQS = [
  { q: "What seating capacities are available?", a: "Published tariffs cover 13 (12+1), 17 (16+1), 20 (19+1), 21 (20+1) and 26 (25+1) total seats, including one driver. A 9-seater option is available on request with capacity and fare confirmed separately. Share your group size and luggage before booking." },
  { q: "How is luggage space confirmed?", a: "Share the number and size of your bags when you enquire. We confirm luggage space and any carrier arrangements for the assigned vehicle before booking." },
  { q: "Do you plan multi-day group tours?", a: "Yes. Share your itinerary, dates and group size and we'll plan the route and give you a quote covering the full trip." },
  { q: "How is the fare calculated?", a: "Published outstation rates range from ₹40 to ₹55 per KM including fuel, with a minimum of 300 KM per day and garage-to-garage billing. Local base hire ranges from ₹4,500 to ₹9,000 for 120 KM / 12 hours, plus fuel. Extra-hour, applicable night charges, 5% GST and listed expenses are additional. Check the vehicle tariff and confirm your final itinerary quotation. The 9-seater is quoted separately." },
];

export default function TempoTraveller() {
  const { t, tParts } = useLanguage();
  return (
    <>
      <Seo
        title={t(STATIC_SERVICE_SEO.tempo.title)}
        description={t(STATIC_SERVICE_SEO.tempo.description)}
        image={IMG.tempo}
        jsonLd={TEMPO_TRAVELLER_SCHEMA}
      />
      <section className="relative overflow-hidden bg-[#0B1E36]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <Eyebrow className="text-amber-400">{t("Group Travel Specialists")}</Eyebrow>
            <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{t(STATIC_SERVICE_SEO.tempo.h1)}</h1>
            <p className="mt-4 text-base sm:text-lg text-slate-300">{t("Arrange Tempo Traveller hire and booking for wedding guests, pilgrimage groups and multi-day tours from Satna. Our taxi services also offer a car or cab for smaller parties; tell us your passenger count and luggage so we can confirm a suitable vehicle.")}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <QuoteButton prefill={{ vehicle_category: "Tempo Traveller" }} testId="tempo-hero-quote-button">{t("Request a Quote")}</QuoteButton>
            </div>
            <p className="mt-3 text-xs text-slate-400"><Luggage className="inline h-3.5 w-3.5 mr-1" />{t("Listed seat totals include one driver. Confirm luggage space and vehicle specifications before booking.")}</p>
          </div>
          <div className="overflow-hidden rounded-2xl shadow-2xl"><OptimizedImage src={IMG.tempo} sizes="(min-width: 1024px) 600px, calc(100vw - 32px)" width="1264" height="848" fetchPriority="high" decoding="async" alt={t("White Force Tempo Traveller minibus on a highway, ideal for group travel from Satna")} className="w-full object-cover" /></div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <SectionHeading eyebrow={t("Perfect for")} title={t("When a Tempo Traveller makes sense")} intro={t("A Tempo Traveller can keep your group on one planned itinerary. Share your group size, luggage and travel dates so we can confirm the seating option and a quotation.")} />
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {USE_CASES.map((u) => (
            <div key={u.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid={`tempo-usecase-${u.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2"><Users className="h-5 w-5 text-amber-600" aria-hidden="true" /> {t(u.title)}</h3>
              <p className="mt-2 text-sm text-slate-600">{t(u.desc)}</p>
            </div>
          ))}
        </div>

        <ServiceBookingOptions page="tempo" className="mt-12" />

        <div className="mt-14" id="traveller-seating">
          <SectionHeading
            eyebrow={t("Compare seating and rates")}
            title={t("Tempo Traveller hire and rental with driver")}
            intro={t("Compare five published Tempo Traveller tariffs. Seat totals include the driver: 13 seater means 12 passengers + 1 driver. The existing 9-seater option needs a separate quote. Confirm availability, luggage space, AC and seat layout for your dates.")}
          />
          <div className="mt-8"><VehicleTariffTable category="Tempo Traveller" /></div>
          <div className="mt-8"><FareTerms /></div>
          <p className="mt-6 text-sm leading-relaxed text-slate-600">
            {tParts("Compare {fleet_link} for a smaller party, or check {areas_link}. Journeys are arranged from our Satna base, with the route, vehicle and fare confirmed in advance.", {
              fleet_link: <Link key="fleet" to="/fleet/" className="font-semibold text-amber-700 underline underline-offset-4">{t("car hire and cab options")}</Link>,
              areas_link: <Link key="areas" to="/service-areas/" className="font-semibold text-amber-700 underline underline-offset-4">{t("pickup information for all service locations")}</Link>,
            })}
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <SectionHeading eyebrow={t("Good to know")} title={t("Tempo Traveller FAQs")} />
          <div className="mt-8"><FaqAccordion faqs={FAQS.map(({ q, a }) => ({ q: t(q), a: t(a) }))} /></div>
          <div className="mt-8 text-center"><QuoteButton prefill={{ vehicle_category: "Tempo Traveller" }} testId="tempo-faq-quote-button">{t("Request a Tempo Traveller quote")}</QuoteButton></div>
        </div>
      </section>
    </>
  );
}
